/* Workforce attendance — heads against the requirement, department by
 * department.
 *
 * Workers and staff summed into one floor, which is the one real departure
 * from the desktop Attendance page: it keeps the two registers apart behind
 * a picker, and there is no room for that picker here. The heading says
 * which month the biometric registers actually reach, which is usually a
 * month behind the tracker.
 */

import type { MobileSummary } from '../../lib/api_mobile'
import { MobileTable, type MRow } from '../MobileTable'
import {
  BAD, Bars, Head, INK, IN, KpiGrid, NoteBox, P1, SG, kpi, type MBar,
} from '../parts'

export function Workforce({ data }: { data: MobileSummary }) {
  const h = data.hr
  if (h.empty) {
    return <NoteBox>No attendance register on file. Load the biometric reports
      on the web app's Data loads page.</NoteBox>
  }

  const attendance = h.totals.presentDays / (h.register * h.dayCount) * 100
  const gap = h.avgPresent - h.req
  const maxP = Math.max(h.req, ...h.days.map((d) => d.p)) * 1.06

  const bars: MBar[] = h.days.map((d) => ({
    h: d.p / maxP * 100,
    lab: d.d % 5 === 0 || d.d === 1 ? String(d.d) : '',
  }))

  const rows: MRow[] = h.depts.map((d) => {
    const g = d.req ? d.avg - d.req : null
    return {
      cells: [
        { v: d.n, sub: d.att < 75 ? 'below target' : undefined },
        { v: P1(d.att), bar: d.att, s: d.att,
          barC: d.att < 75 ? 'var(--c-ink)' : 'var(--c-bar1)' },
        { v: d.avg.toFixed(1) },
        { v: d.req ? String(d.req) : '–' },
        { v: g == null ? '–' : SG(g, (x) => x.toFixed(0)), s: g ?? 0,
          color: g != null && g < 0 ? BAD : INK },
        { v: String(d.active) },
        { v: P1(d.late), s: d.late, color: d.late > 30 ? BAD : INK },
        { v: String(d.miss) },
        { v: IN(d.ot) },
      ],
    }
  })

  return (
    <>
      <div className="m-label" style={{ padding: '12px 0 2px' }}>
        {h.month} · {h.dayCount} days
      </div>

      <KpiGrid style={{ marginTop: 16 }} items={[
        kpi('Present / day', IN(h.avgPresent), `${h.req} required · ${h.register} on roll`),
        kpi('Manning gap', SG(gap, IN),
          h.req ? `${P1(Math.abs(gap) / h.req * 100)} ${gap < 0 ? 'short of' : 'over'} requirement` : '',
          gap < 0),
        kpi('Attendance', P1(attendance), `of ${h.register} active on roll`, attendance < 80),
        kpi('Late arrivals', P1(h.totals.late / (h.totals.presentDays || 1) * 100),
          `${IN(h.totals.late)} of ${IN(h.totals.presentDays)} present days`),
        kpi('Punch misses', IN(h.totals.miss), 'no in / no out / wrong punch'),
        kpi('Overtime', `${IN(h.totals.ot)} h`, `${IN(h.totals.ot / h.dayCount)} h a day`),
      ]} />

      <Head title="Heads present by day" aside="heads" />
      <div className="m-aside" style={{ marginTop: -6, marginBottom: 12 }}>
        Dashed line is the {h.req} required
      </div>
      <Bars bars={bars} gap={2} refLine={h.req / maxP * 100} legend={[
        { t: 'Present', c: 'var(--c-bar1)' },
        { t: 'Required', border: '1px dashed var(--c-ink)', line: true },
      ]} />

      <Head title="Department by department" aside="largest first" />
      <MobileTable cols={[
        { l: 'Department', w: 130 }, { l: 'Attendance', w: 100 }, { l: 'Present', w: 64 },
        { l: 'Req', w: 48 }, { l: 'Gap', w: 54 }, { l: 'Roll', w: 50 },
        { l: 'Late', w: 56 }, { l: 'Miss', w: 50 }, { l: 'OT h', w: 62 },
      ]} rows={rows} />

      <div className="m-aside" style={{ marginTop: 10 }}>
        Workers and staff together. Attendance is present days against the
        roll over the month; late and miss are the biometric punches.
      </div>
    </>
  )
}
